import React from 'react';
import {
  Badge, Button, CellText, EmptyState, ProgressBar, Table,
} from '@opendate-app/setlist';
import formatPledgeAmount from '../../../shared/utils/formatPledgeAmount';

const fmt = (cents) => formatPledgeAmount(cents);

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const STATUSES = {
  active:         { label: 'Active',         color: 'brand' },
  partially_paid: { label: 'Partially Paid', color: 'warning' },
  delinquent:     { label: 'Delinquent',     color: 'error' },
  fulfilled:      { label: 'Fulfilled',      color: 'success' },
  written_off:    { label: 'Written Off',    color: 'gray' },
};

export default function PledgeList({ pledges, loading, teamId, onEdit, onCancel, canManage }) {
  const columns = [
    {
      key: 'name',
      header: 'Pledge',
      primary: true,
      render: (p) => (
        <CellText
          primary={(
            <Button as="a" href={`/teams/${teamId}/crm/pledges/${p.id}`} variant="link-color">
              {p.name}
            </Button>
          )}
          supporting={[
            p.display_number,
            p.installment_frequency.replace('_', '-'),
            p.card && `${p.card.brand} •••• ${p.card.last4}`,
          ].filter(Boolean).join(' · ')}
        />
      ),
    },
    {
      key: 'fund',
      header: 'Fund / Campaign',
      render: (p) => <CellText primary={p.fund?.name} supporting={p.giving_campaign?.name} />,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (p) => fmt(p.total_amount_cents),
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (p) => <ProgressBar value={p.progress_percent} label="right" />,
    },
    {
      key: 'next_due',
      header: 'Next due',
      render: (p) => (p.overdue_amount_cents > 0
        ? <span className="text-danger">{fmt(p.overdue_amount_cents)} overdue</span>
        : (formatDate(p.next_due_on) || '—')),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => (
        <div className="d-flex flex-wrap" style={{ gap: 4 }}>
          <Badge color={STATUSES[p.status].color} dot>{STATUSES[p.status].label}</Badge>
          {p.failed_installments_count > 0 && <Badge color="error">Payment failed</Badge>}
        </div>
      ),
    },
  ];

  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      render: (p) => (
        <div className="d-flex justify-content-end">
          <Button variant="link-gray" onClick={() => onEdit(p)}>Edit</Button>
          <span className="ml-3">
            <Button variant="link-destructive" onClick={() => onCancel(p)}>Cancel</Button>
          </span>
        </div>
      ),
    });
  }

  return (
    <Table
      columns={columns}
      data={pledges}
      loading={loading}
      getRowKey={(p) => p.id}
      empty={<EmptyState size="sm" title="No pledges yet." />}
    />
  );
}
